import { useState } from 'react';
import { Play, Trash2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button, IconButton, Sheet, SheetContent, SheetFooter, SheetTitle } from '@/components/ui';
import { useIsAtLeast } from '@/hooks/useBreakpoint';
import { useTaskStore } from '@/stores/taskStore';
import { useTaskAction } from '@/features/lifecycle/useTaskAction';
import { SkipReasonGrid } from '@/features/lifecycle/SkipReasonGrid';
import { hapticTap, hapticSkip } from '@/features/lifecycle/haptics';
import type { SkipReason } from '@/types';
import { TitleField } from './fields/TitleField';
import { NotesField } from './fields/NotesField';
import { PriorityField } from './fields/PriorityField';
import { EnergyField } from './fields/EnergyField';
import { DurationField } from './fields/DurationField';
import { ScheduleField } from './fields/ScheduleField';
import { TagsField } from './fields/TagsField';
import { SubtaskList } from './fields/SubtaskList';
import { useEditorOpen } from './useEditorOpen';

export function TaskSlideOver() {
  const { open, task, closeEditor } = useEditorOpen();
  const isDesktop = useIsAtLeast('md');
  const setStatus = useTaskStore((s) => s.setStatus);
  const { complete, skip, remove, reopen } = useTaskAction();
  const [showSkipReasons, setShowSkipReasons] = useState(false);

  const onDelete = () => {
    if (!task) return;
    remove(task.id);
    closeEditor();
  };

  const onStart = () => {
    if (!task) return;
    setStatus(task.id, 'in_progress');
    void hapticTap();
    toast('Started');
  };

  const onSkip = (reason: SkipReason) => {
    if (!task) return;
    void hapticSkip();
    skip(task.id, reason);
    setShowSkipReasons(false);
    closeEditor();
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        if (!v && showSkipReasons) {
          setShowSkipReasons(false);
          return;
        }
        if (!v) closeEditor();
      }}
    >
      <SheetContent
        side={isDesktop ? 'right' : 'bottom'}
        onPointerDownOutside={(e) => {
          if (showSkipReasons) e.preventDefault();
        }}
      >
        {task ? (
          <div className="flex h-full flex-col overflow-hidden">
            <SheetTitle className="sr-only">{task.title || 'Untitled task'}</SheetTitle>
            {/* The Sheet's close (×) is absolutely positioned in the top-right
                corner, on top of this row — reserve its footprint so the title
                input (and its inset focus ring) never runs beneath the button.
                Fine pointer: 12px inset + 28px button − 20px sheet padding =
                20px intrusion → pr-8. Coarse pointer: the × grows to 44px →
                36px intrusion → pr-12. */}
            <div className="pr-8 pointer-coarse:pr-12">
              <TitleField task={task} />
            </div>
            {/* px-1 with the -mx-1 counter-margin gives ring-2 + ring-offset-2
                (4px) room inside the scroll clip box — without it the fields'
                focus rings are shaved off at the container edges. */}
            <div className="-mx-1 mt-4 flex-1 space-y-4 overflow-y-auto px-1">
              <ScheduleField task={task} />
              <div className="grid grid-cols-2 gap-3">
                <PriorityField task={task} />
                <EnergyField task={task} />
              </div>
              <DurationField task={task} />
              <TagsField task={task} />
              <NotesField task={task} />
              <SubtaskList task={task} />
            </div>
            <div className={showSkipReasons ? 'border-t border-line px-1 pb-2 pt-3' : 'hidden'}>
              <SkipReasonGrid onPick={onSkip} />
            </div>
            <SheetFooter className="flex-wrap">
              <IconButton
                label="Delete"
                variant="ghost"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={onDelete}
              />
              {task.status === 'done' || task.status === 'skipped' ? (
                // Persistent recovery once the 5s undo toast is gone.
                <Button
                  variant="primary"
                  leadingIcon={<Undo2 className="h-3.5 w-3.5" aria-hidden />}
                  onClick={() => reopen(task.id)}
                >
                  Reopen
                </Button>
              ) : (
                <>
                  <Button variant="secondary" onClick={() => setShowSkipReasons(!showSkipReasons)}>
                    {showSkipReasons ? 'Cancel' : 'Skip'}
                  </Button>
                  {task.status !== 'in_progress' && (
                    <Button variant="secondary" onClick={onStart}>
                      <Play className="h-3.5 w-3.5" />
                      <span className="ml-1">Start now</span>
                    </Button>
                  )}
                  <Button variant="primary" onClick={() => complete(task.id)}>
                    Mark done
                  </Button>
                </>
              )}
            </SheetFooter>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
